import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

// Neon color palette for multi-symbol mode
const MULTI_COLORS = [
  { border: "#7b61ff", bg: "rgba(123, 97, 255, 0.08)" },    // electric purple
  { border: "#00e676", bg: "rgba(0, 230, 118, 0.08)" },     // neon green
  { border: "#ff3d71", bg: "rgba(255, 61, 113, 0.08)" },    // hot pink
  { border: "#00d4ff", bg: "rgba(0, 212, 255, 0.08)" },     // cyan
  { border: "#ffb400", bg: "rgba(255, 180, 0, 0.08)" },     // amber
  { border: "#e040fb", bg: "rgba(224, 64, 251, 0.08)" },    // magenta
  { border: "#00bfa5", bg: "rgba(0, 191, 165, 0.08)" },     // teal
];

function emptyChart(h) {
  return (
    <div
      style={{
        textAlign: "center",
        height: `${h}px`,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#12121a",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.06)",
      }}>
      <div>
        <p style={{ fontSize: "16px", color: "#8888a0", fontFamily: "'JetBrains Mono', monospace" }}>No price data available</p>
        <p style={{ fontSize: "13px", color: "#5a5a72" }}>Waiting for market data...</p>
      </div>
    </div>
  );
}

function PriceChart({
  priceHistory = [],
  symbol = "A",
  allPriceHistory = {},
  mode = "single",
  height = 420,
  compact = false,
}) {
  if (mode === "multi") {
    const symbols = Object.keys(allPriceHistory).filter(
      (sym) => (allPriceHistory[sym] || []).length > 0
    );

    if (symbols.length === 0) {
      return emptyChart(height);
    }

    const datasets = symbols.map((sym, i) => {
      const color = MULTI_COLORS[i % MULTI_COLORS.length];
      const prices = allPriceHistory[sym] || [];
      return {
        label: sym,
        data: prices.map((point) => ({ x: point.timestamp, y: point.price })),
        borderColor: color.border,
        backgroundColor: color.bg,
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: color.border,
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
      };
    });

    const multiOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: true, position: "top", labels: { color: "#8888a0", font: { family: "'JetBrains Mono', monospace", size: 11 }, boxWidth: 12, padding: 16 } },
        title: {
          display: true,
          text: "All Symbols — Price History",
          font: { size: 16, weight: "bold", family: "'JetBrains Mono', monospace" },
          color: "#e8e8f0",
          padding: { top: 10, bottom: 20 },
        },
        tooltip: {
          backgroundColor: "rgba(18, 18, 26, 0.95)", borderColor: "rgba(255,255,255,0.06)", borderWidth: 1,
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: "minute",
            displayFormats: { minute: "HH:mm:ss" },
            tooltipFormat: "HH:mm:ss",
          },
          grid: { display: true, color: "rgba(255, 255, 255, 0.03)", drawBorder: false },
          title: {
            display: true,
            text: "Time",
            font: { size: 12 },
            color: "#6b6b7b",
            padding: 8,
          },
          ticks: { maxRotation: 0, autoSkip: true, color: "#6b6b7b" },
        },
        y: {
          beginAtZero: false,
          grid: { display: true, color: "rgba(255, 255, 255, 0.03)", drawBorder: false },
          title: {
            display: true,
            text: "Price ($)",
            font: { size: 12 },
            color: "#6b6b7b",
            padding: 8,
          },
          ticks: {
            color: "#6b6b7b",
            callback: (value) => "$" + value,
          },
        },
      },
    };

    return (
      <div style={{ position: "relative", height: `${height}px`, width: "100%" }}>
        <Line options={multiOptions} data={{ datasets }} />
      </div>
    );
  }

  // --- Single mode ---
  const prices = Array.isArray(priceHistory) ? priceHistory : [];

  if (prices.length === 0) {
    return emptyChart(height);
  }

  const isPriceUp =
    prices.length >= 2 && prices[prices.length - 1].price > prices[0].price;

  const primaryColor = isPriceUp ? "#00e676" : "#ff3d71";
  const secondaryColor = isPriceUp
    ? "rgba(0, 230, 118, 0.1)"
    : "rgba(255, 61, 113, 0.1)";

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      title: {
        display: !compact,
        text: `${symbol} Price History`,
        font: { size: 16, weight: "bold", family: "'JetBrains Mono', monospace" },
        color: "#e8e8f0",
        padding: { top: 10, bottom: 20 },
      },
      tooltip: {
        backgroundColor: "rgba(18, 18, 26, 0.95)", borderColor: "rgba(255,255,255,0.06)", borderWidth: 1,
        titleFont: { size: 13 },
        bodyFont: { size: 12, family: "'JetBrains Mono', monospace" },
        titleColor: "#e8e8f0",
        bodyColor: "#8888a0",
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function (context) {
            return `Price: $${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "minute",
          displayFormats: { minute: "HH:mm:ss" },
          tooltipFormat: "HH:mm:ss",
        },
        grid: { display: true, color: "rgba(255, 255, 255, 0.03)", drawBorder: false },
        title: {
          display: !compact,
          text: "Time",
          font: { size: 14 },
          padding: 10,
        },
        ticks: { maxRotation: 0, autoSkip: true, color: "#6b6b7b", display: !compact },
      },
      y: {
        beginAtZero: false,
        grid: { display: true, color: "rgba(255, 255, 255, 0.03)", drawBorder: false },
        title: {
          display: !compact,
          text: "Price ($)",
          font: { size: 14 },
          padding: 10,
        },
        ticks: {
          color: "#6b6b7b",
          callback: (value) => "$" + value,
        },
      },
    },
  };

  const data = {
    datasets: [
      {
        label: symbol,
        data: prices.map((point) => ({ x: point.timestamp, y: point.price })),
        borderColor: primaryColor,
        backgroundColor: secondaryColor,
        borderWidth: 2.5,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: primaryColor,
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
      },
    ],
  };

  return (
    <div style={{ position: "relative", height: `${height}px`, width: "100%" }}>
      <Line options={options} data={data} />
    </div>
  );
}

export default PriceChart;
